/**
 * Terrain déterministe de la vallée.
 *
 * Le relief est une fonction pure de la graine de carte : le serveur l'utilise
 * pour valider les constructions et calculer les pistes, le client pour le
 * maillage et pour poser skieurs, pylônes et décor. Aucun relief ne transite
 * sur le réseau.
 *
 * Repère : x vers l'est, z vers le sud (vers la caméra par défaut), y vers le
 * haut. Sur Val Cendrée, la montagne monte vers le nord (z négatif) et le
 * village est au sud ; les cartes « massif » placent leurs arêtes librement.
 *
 * Chaque carte a sa taille (carrée, centrée sur l'origine) : le champ de
 * hauteur en porte les dimensions, et tout le code lit `hf.half`, `hf.res`
 * et `hf.cell` plutôt qu'une constante.
 */
import { createNoise2D, fbm, ridged, smoothstep, clamp, lerp, swave } from './noise';
import { createMassifFunction, type MassifRelief } from './relief';

/** Taille de la carte par défaut, Val Cendrée (m). */
export const MAP_SIZE = 2048;
export const MAP_HALF = MAP_SIZE / 2;
/** Pas du champ de hauteur échantillonné (m), identique pour toutes les cartes. */
export const HF_CELL = 4;
/** Nombre d'échantillons par côté de la carte par défaut. */
export const HF_RES = MAP_SIZE / HF_CELL + 1;

export interface MapFeatures {
  seed: number;
  /** Côté de la carte (m), multiple de 256 ; 2 048 par défaut. */
  size?: number;
  /** Relief décrit par ses arêtes ; absent : la vallée de Val Cendrée. */
  relief?: MassifRelief;
  village: { x: number; z: number; radius: number };
  lake: { x: number; z: number; radius: number };
}

/** Côté de la carte décrite (m). */
export function mapSize(features: MapFeatures): number {
  return features.size ?? MAP_SIZE;
}

/** Relief de Val Cendrée : une face skiable unique qui monte vers le nord. */
function createValleyFunction(seed: number): (x: number, z: number) => number {
  const n1 = createNoise2D(seed + 11);
  const n2 = createNoise2D(seed + 23);
  const n3 = createNoise2D(seed + 37);
  const n4 = createNoise2D(seed + 51);
  const n5 = createNoise2D(seed + 67);

  return (x: number, z: number): number => {
    const u = clamp((480 - z) / 1480, 0, 1);
    const u2 = u * u;
    // Profil principal : pente douce en bas (débutants), plus raide vers les crêtes.
    const base = 470 * Math.pow(u, 1.6);
    // Sommets et arêtes en haut du domaine.
    const peaks = 95 * ridged(n1, x / 600 + 3.7, z / 600 - 1.3, 4) * smoothstep(0.55, 1, u) * u2;
    // Éperons et combes à grande échelle.
    const spur = 40 * swave(x / 900 + 0.15) * smoothstep(0.15, 0.7, u);
    // Couloirs naturels (futures pistes).
    const g = 1 - Math.abs(swave(x / 460 + 0.37 + 0.1 * n2(x / 400, z / 400)));
    const gully = -20 * g * g * (3 - 2 * g) * u * (1 - u) * 4;
    // Détail fin, plus marqué en altitude.
    const detail = fbm(n3, x / 190, z / 190, 4) * (3 + 9 * u);
    // Flancs de la vallée, plus hauts vers le nord.
    const flank = smoothstep(620, 1010, Math.abs(x)) * (160 + 220 * u) * (0.75 + 0.25 * fbm(n4, x / 300, z / 300, 3));
    // Collines fermant la vallée au sud.
    const south = 90 * smoothstep(860, 1024, z) * (0.8 + 0.2 * n4(x / 200 + 5, z / 200));
    // Fond de vallée légèrement ondulé.
    const floor = 6 * fbm(n5, x / 240, z / 240, 3) * (1 - u);
    return 12 + base + peaks + spur + gully + detail + flank + south + floor;
  };
}

/** Fonction de relief analytique (lente : sert à remplir le champ de hauteur). */
export function createTerrainFunction(features: MapFeatures): (x: number, z: number) => number {
  const raw = features.relief ? createMassifFunction(features.relief, features.seed) : createValleyFunction(features.seed);

  const v = features.village;
  const villageH = raw(v.x, v.z);
  const l = features.lake;
  const lakeH = raw(l.x, l.z);

  return (x: number, z: number): number => {
    let h = raw(x, z);
    // Plateau du village (constructible).
    const dv = Math.hypot(x - v.x, z - v.z);
    const wv = smoothstep(v.radius, v.radius * 0.55, dv);
    h = lerp(h, villageH, wv);
    // Cuvette du lac.
    const dl = Math.hypot(x - l.x, z - l.z);
    const wl = smoothstep(l.radius, l.radius * 0.25, dl);
    h = lerp(h, Math.min(h, lakeH), wl) - wl * 9;
    return h;
  };
}

/** Niveau de l'eau du lac (m) : 7,5 m au-dessus du fond de la cuvette. */
export function lakeLevel(features: MapFeatures, fn: (x: number, z: number) => number): number {
  return fn(features.lake.x, features.lake.z) + 7.5;
}

/**
 * Champ de hauteur échantillonné (pas de 4 m) avec interpolation bilinéaire.
 * C'est la référence unique du relief : le maillage client est construit sur
 * les mêmes échantillons, donc un skieur posé avec `height()` touche la neige.
 */
export class Heightfield {
  /** Côté de la carte (m). */
  readonly size: number;
  readonly res: number;
  readonly cell = HF_CELL;
  readonly half: number;
  readonly data: Float32Array;
  minH = Infinity;
  maxH = -Infinity;
  waterLevel = -Infinity;

  /** `size` se déduit des données quand il est omis (copie d'un champ existant). */
  constructor(data?: Float32Array, size?: number) {
    const s = size ?? (data ? Math.round(Math.sqrt(data.length) - 1) * HF_CELL : MAP_SIZE);
    this.size = s;
    this.half = s / 2;
    this.res = s / HF_CELL + 1;
    if (data && data.length !== this.res * this.res) throw new Error('Champ de hauteur : taille incohérente');
    this.data = data ?? new Float32Array(this.res * this.res);
  }

  static fromFunction(fn: (x: number, z: number) => number, size: number = MAP_SIZE): Heightfield {
    const hf = new Heightfield(undefined, size);
    const { res, cell, half, data } = hf;
    for (let j = 0; j < res; j++) {
      const z = -half + j * cell;
      for (let i = 0; i < res; i++) {
        const h = fn(-half + i * cell, z);
        data[j * res + i] = h;
        if (h < hf.minH) hf.minH = h;
        if (h > hf.maxH) hf.maxH = h;
      }
    }
    return hf;
  }

  /** Hauteur de l'échantillon (i, j), indices bornés. */
  at(i: number, j: number): number {
    const r = this.res - 1;
    const ii = i < 0 ? 0 : i > r ? r : i;
    const jj = j < 0 ? 0 : j > r ? r : j;
    return this.data[jj * this.res + ii] as number;
  }

  /** Hauteur interpolée en (x, z). */
  height(x: number, z: number): number {
    const fx = (x + this.half) / this.cell;
    const fz = (z + this.half) / this.cell;
    const r = this.res - 1;
    const cx = fx < 0 ? 0 : fx > r ? r : fx;
    const cz = fz < 0 ? 0 : fz > r ? r : fz;
    let i = Math.floor(cx);
    let j = Math.floor(cz);
    if (i >= r) i = r - 1;
    if (j >= r) j = r - 1;
    const tx = cx - i;
    const tz = cz - j;
    const d = this.data;
    const k = j * this.res + i;
    const h00 = d[k] as number;
    const h10 = d[k + 1] as number;
    const h01 = d[k + this.res] as number;
    const h11 = d[k + this.res + 1] as number;
    const a = h00 + (h10 - h00) * tx;
    const b = h01 + (h11 - h01) * tx;
    return a + (b - a) * tz;
  }

  /** Gradient (dh/dx, dh/dz) par différences centrées. */
  gradient(x: number, z: number, step = this.cell): [number, number] {
    const gx = (this.height(x + step, z) - this.height(x - step, z)) / (2 * step);
    const gz = (this.height(x, z + step) - this.height(x, z - step)) / (2 * step);
    return [gx, gz];
  }

  /** Pente (rapport hauteur / distance, 1 = 45 degrés). */
  slope(x: number, z: number): number {
    const [gx, gz] = this.gradient(x, z);
    return Math.sqrt(gx * gx + gz * gz);
  }

  /** Normale unitaire [nx, ny, nz]. */
  normal(x: number, z: number): [number, number, number] {
    const [gx, gz] = this.gradient(x, z);
    const len = Math.sqrt(gx * gx + 1 + gz * gz);
    return [-gx / len, 1 / len, -gz / len];
  }

  inBounds(x: number, z: number, margin = 0): boolean {
    return x >= -this.half + margin && x <= this.half - margin && z >= -this.half + margin && z <= this.half - margin;
  }

  /**
   * Test de visibilité : vrai si le relief coupe le segment a -> b (occlusion).
   * Échantillonne le segment ; sert au culling d'occlusion côté client.
   */
  occludes(ax: number, ay: number, az: number, bx: number, by: number, bz: number, samples = 24): boolean {
    for (let s = 1; s < samples; s++) {
      const t = s / samples;
      const x = ax + (bx - ax) * t;
      const y = ay + (by - ay) * t;
      const z = az + (bz - az) * t;
      if (this.height(x, z) > y + 2) return true;
    }
    return false;
  }
}

const cache = new Map<string, Heightfield>();

/** Champ de hauteur d'une carte, mis en cache (une carte = un relief). */
export function getHeightfield(features: MapFeatures): Heightfield {
  const key = JSON.stringify(features);
  let hf = cache.get(key);
  if (!hf) {
    const fn = createTerrainFunction(features);
    hf = Heightfield.fromFunction(fn, mapSize(features));
    hf.waterLevel = lakeLevel(features, fn);
    cache.set(key, hf);
  }
  return hf;
}
